import {reportTypo} from '@api/typo';
import Fade from '@components/Animation/Fade';
import Snackbar from '@components/Snackbar';
import type {SnackbarMessage} from '@interfaces/Snackbar';
import {getRefElement} from '@utils/refUtils';
import throttle from 'lodash.throttle';
import type {ReactElement} from 'react';
import {useEffect, useRef, useState} from 'react';
import {localize} from '@services/i18n';
import I18n from '@components/I18n';

const THROTTLE_INTERVAL = 16;
const CONTENT_WIDTH = 1000;

export default function TypoButton(): ReactElement {
    const [snackbarMessage, setSnackbarMessage] = useState<SnackbarMessage>();
    const [leftIndent, setLeftIndent] = useState(0);
    const [isVisible, setIsVisible] = useState(false);
    const rootRef = useRef(null);

    useEffect(() => {
        const throttledCalculatePosition = throttle(calculatePosition, THROTTLE_INTERVAL);

        window.addEventListener('resize', throttledCalculatePosition);
        document.addEventListener('selectionchange', handleSelectionChange);
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('resize', throttledCalculatePosition);
            document.removeEventListener('selectionchange', handleSelectionChange);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    useEffect(calculatePosition, []);

    function calculatePosition() {
        try {
            const refElement = getRefElement<HTMLElement>(rootRef);
            const parentPosition = refElement.closest('article')!.getBoundingClientRect();
            setLeftIndent(
                parentPosition.left +
                    Math.min((parentPosition.width + CONTENT_WIDTH) / 2, parentPosition.width),
            );
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (error) {
            // welp
        }
    }

    function handleSelectionChange() {
        const selection = window.getSelection();
        setIsVisible(
            selection ? selection.toString().length > 0 && isInTextContent(selection) : false,
        );
    }

    function handleKeyDown(event: KeyboardEvent) {
        if (event.ctrlKey && event.key === 'Enter') {
            handleClick();
        }
    }

    function handleClick() {
        const selection = window.getSelection();

        if (selection && isInTextContent(selection)) {
            reportTypo(
                location.pathname,
                selection.toString(),
                selection.getRangeAt(0).commonAncestorContainer.textContent?.trim() ?? '',
                Math.max(selection.anchorOffset, selection.focusOffset),
            );
            selection.empty();
            setSnackbarMessage({text: localize('Typo_successMessage')});
        }
    }

    function isInTextContent(selection: Selection | null): boolean {
        if (!selection?.toString().trim().length) {
            return false;
        }

        let currentElement: Element | null =
            selection.getRangeAt(0).commonAncestorContainer.parentElement;

        while (currentElement) {
            if (currentElement.classList.contains('js-typo-button-root')) {
                return true;
            }
            currentElement = currentElement.parentElement;
        }
        return false;
    }

    return (
        <div ref={rootRef}>
            <Fade in={isVisible} unmountOnExit>
                <button
                    type="button"
                    className="typo-button"
                    onClick={handleClick}
                    style={{left: leftIndent}}
                >
                    <I18n code="Typo_sendButtonLabel" />
                </button>
            </Fade>
            <Snackbar {...snackbarMessage} />
        </div>
    );
}
